<template>
  <div class="border-2 border-gray-800">
    <div class="overflow-hidden text-base bg-gray-800 text-white px-4 py-3">
      <div class="float-left">
        Employment {{ index + 1 }}
      </div>

      <div v-if="(index > 0 && !employment.previous) ||
                 (employment.previous && !isCurrentLessThanThreeYears) ||
                 (employment.previous && isCurrentLessThanThreeYears && numPreviousEmployments > 1)"
        class="float-right">
        <a href="#"
          @click.prevent="deleteEmployment(employment.id, index)"
          class="no-underline text-sm text-white hover:underline">
          <span class="">Delete</span>
        </a>
      </div>
    </div>

    <div class="p-4">
      <div class="flex flex-wrap">
        <div class="w-full lg:w-1/3 pr-0 lg:pr-3">
          <EmploymentStatusSelector
            propertyKey="status"
            :value="employment.status"
            objectType="employment"
            :index="index"
            :updateValue="updateValue"
            :required="true"></EmploymentStatusSelector>
        </div>

        <div class="w-full lg:w-1/3 pr-0 lg:pr-3 mt-8 lg:mt-0">
          <InputText label="Employer's name"
            propertyKey="name"
            :value="employment.name"
            objectType="employment"
            :index="index"
            :updateValue="updateValue"
            :required="true" />
        </div>

        <div class="w-full lg:w-1/3 mt-8 lg:mt-0">
          <InputABN label="Employer's ABN"
            propertyKey="abn"
            tooltip="11 digit number on your payslip."
            :value="employment.abn"
            objectType="employment"
            :index="index"
            :updateValue="updateValue"></InputABN>
        </div>
      </div>

      <div class="flex flex-wrap label-line">
        <div class="w-full lg:w-1/3 pr-0 lg:pr-3 flex flex-wrap">
          <div class="w-1/3 pr-2">
            <BuildingTypeSelector
              propertyKey="building_type"
              objectType="address"
              :index="index"
              :value="employment.building_type"
              :updateValue="updateValue" />
          </div>

          <div class="w-1/3 pr-2">
            <InputText 
              label="Unit number"
              propertyKey="unit_number"
              objectType="address"
              :index="index"
              :value="employment.unit_number"
              :updateValue="updateValue" />
          </div>

          <div class="w-1/3">
            <InputText 
              label="Street number"
              propertyKey="street_number"
              objectType="address"
              :index="index"
              :value="employment.street_number"
              :updateValue="updateValue" />
          </div>
        </div>

        <div class="w-full lg:w-1/3 pr-0 lg:pr-3 mt-8 lg:mt-0 flex flex-wrap">
          <div class="w-3/4 pr-2">
            <InputText 
              label="Street name"
              propertyKey="street_name"
              objectType="address"
              :index="index"
              :value="employment.street_name"
              :updateValue="updateValue" />

            <div class="mt-1 text-right" v-if="employment.street_name != null && employment.street_name.length > 0">
              <a href="#" class="text-gray-700 hover:underline" @click.prevent="$modal.show('map-modal', { 
                address : getAddressText() 
              })">Show this address on map</a>
            </div>
          </div>

          <div class="w-1/4 pl-1">
            <StreetTypeSelector
              propertyKey="street_type"
              objectType="address"
              :index="index"
              :value="employment.street_type"
              :updateValue="updateValue" />
          </div>
        </div>

        <div class="w-full lg:w-1/3 pr-0 lg:pr-3 mt-8 lg:mt-0">
          <InputText 
            label="Suburb" 
            objectType="address"
            propertyKey="suburb"
            :index="index"
            :value="employment.suburb"
            :updateValue="updateValue" />
        </div>
      </div>

      <div class="flex flex-wrap label-line">
        <div class="w-full lg:w-1/3 pr-0 lg:pr-3">
          <InputPostcode 
            label="Postcode" 
            objectType="address"
            propertyKey="postcode"
            :index="index"
            :value="employment.postcode"
            :updateValue="updateValue" />
        </div>

        <div class="w-full lg:w-1/3 mt-8 lg:mt-0 pr-0 lg:pr-3">
          <StateSelector
            objectType="address"
            propertyKey="state_id"
            :index="index"
            :value="employment.state_id"
            :updateValue="updateValue" />
        </div>

        <div class="w-full lg:w-1/3 pr-0 lg:pr-3 mt-8 lg:mt-0">
          <CountrySelector
            objectType="address"
            propertyKey="country_id"
            :index="index"
            :value="employment.country_id"
            :updateValue="updateValue" />
        </div>
      </div>
      <div class="flex flex-wrap label-line">
        <div class="w-full lg:w-1/3 pr-0 lg:pr-3 mt-8 lg:mt-0">
          <InputDatePickerReadOnly 
            label="Date started"
            propertyKey="date_started_at"
            objectType="employment"
            :index="index"
            :value="employment.date_started_at"
            :updateValue="updateValue"
            :required="true" />

          <div class="mt-2 text-sm text-red-500" v-if="isStartedLaterThanEnded">
            This should be earlier than date ended
          </div>
        </div>

        <div v-if="employment.previous" class="w-full lg:w-1/3 pr-0 lg:pr-3 mt-8 lg:mt-0">
          <InputDatePickerReadOnly 
            label="Date ended"
            propertyKey="date_ended_at"
            objectType="employment"
            :index="index"
            :value="employment.date_ended_at"
            :updateValue="updateValue" />
        </div>

        <div class="w-full lg:w-1/3 pr-0 lg:pr-3 mt-8 lg:mt-0">
          <div class="label-standard">
            How long there?
          </div>

          <div class="mt-2">
            <div class="w-full border bg-gray-200 px-3" style="height:42px;line-height:42px;">
              {{ getEmploymentDuration(employment) }}
            </div>
          </div>
        </div>

        <div v-if="!employment.previous && isDateStartedLessThanSixMonths" class="w-full lg:w-1/3 pr-0 lg:pr-3 mt-8 lg:mt-0">
          <div class="label-standard flex items-center">
            <RequiredAsterisk />
            Have you finished probation?
          </div>

          <div class="mt-2 flex items-center">
            <InputRadio
              name="inputProbation"
              id="inputProbationYes"
              propertyKey="finished"
              objectType="employment"
              :index="index"
              value="1"
              :checked="employment.finished == 1"
              :updateValue="updateValue" />

            <label class="text-base ml-2 mr-8" for="inputProbationYes">Yes</label>

            <InputRadio
              name="inputProbation"
              id="inputProbationNo"
              propertyKey="finished"
              objectType="employment"
              :index="index"
              value="0"
              :checked="employment.finished == null || employment.finished == 0"
              :updateValue="updateValue" />

            <label class="text-base ml-2" for="inputProbationNo">No</label>
          </div>
        </div>
      </div>

      <div class="flex flex-wrap label-line">
        <div class="w-full lg:w-1/3 pr-0 lg:pr-3">
          <InputDollarValue
            label="Annual income"
            propertyKey="income"
            objectType="employment"
            :objectId="employment.id"
            :index="index"
            :value="employment.income"
            :updateValue="updateValue"
            :required="true"/>
        </div>

        <div class="w-full lg:w-1/3 pr-0 lg:pr-3 mt-8 lg:mt-0">
          <InputText
            label="Your occupation"
            propertyKey="occupation"
            objectType="employment"
            :value="employment.occupation"
            :index="index"
            :updateValue="updateValue"
            :required="true" />
        </div>

        <div class="w-full lg:w-1/3 pr-0 lg:pr-3 mt-8 lg:mt-0">
          <InputText
            label="Nature of business"
            propertyKey="nature"
            :value="employment.nature"
            objectType="employment"
            :index="index"
            :updateValue="updateValue" />
        </div>
      </div>

      <div class="flex flex-wrap label-line">
        <div class="w-full lg:w-1/3 pr-0 lg:pr-3">
          <TitleSelector
            label="Employer contact person title"
            propertyKey="contact_title"
            objectType="employment"
            :index="index"
            :value="employment.contact_title"
            :updateValue="updateValue"
            :required="true"></TitleSelector>
        </div>

        <div class="w-full lg:w-1/3 pr-0 lg:pr-3 mt-8 lg:mt-0">
          <InputText
            label="First name"
            propertyKey="contact_first"
            objectType="employment"
            :index="index"
            :value="employment.contact_first"
            :updateValue="updateValue"
            :required="true" />
        </div>

        <div class="w-full lg:w-1/3 pr-0 lg:pr-3 mt-8 lg:mt-0">
          <InputText
            label="Surname"
            propertyKey="contact_last"
            objectType="employment"
            :index="index"
            :value="employment.contact_last"
            :updateValue="updateValue"
            :required="true" />
        </div>
      </div>

      <div class="flex flex-wrap label-line">
        <div class="w-full lg:w-1/3 pr-0 lg:pr-3">
          <InputPhone label="Phone"
            propertyKey="phone"
            objectType="employment"
            :index="index"
            :value="employment.phone"
            :updateValue="updateValue"
            :required="true" />
        </div>

        <div class="w-full lg:w-1/3 pr-0 lg:pr-3 mt-8 lg:mt-0">
          <InputEmail
            label="Email address"
            propertyKey="contact_email"
            objectType="employment"
            :index="index"
            :value="employment.contact_email"
            :updateValue="updateValue"
            :required="true" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import InputText                from '@/shared/components/form/InputText'
  import InputEmail               from '@/shared/components/form/InputEmail'
  import InputPhone               from '@/shared/components/form/InputPhone'
  import InputPostcode            from '@/shared/components/form/InputPostcode'
  import TitleSelector            from '@/shared/components/form/TitleSelector'
  import InputABN                 from '@/shared/components/form/InputABN'
  import InputRadio               from '@/shared/components/form/InputRadio'
  import InputDollarValue         from '@/shared/components/form/InputDollarValue'
  import StateSelector            from '@/shared/components/form/StateSelector'
  import CountrySelector          from '@/shared/components/form/CountrySelector'
  import StreetTypeSelector       from '@/shared/components/form/StreetTypeSelector'
  import InputDatePickerReadOnly  from '@/shared/components/form/InputDatePickerReadOnly'
  import RequiredAsterisk         from '@/shared/components/form/RequiredAsterisk'
  import EmploymentStatusSelector from '../EmploymentStatusSelector'
  import BuildingTypeSelector     from '@/shared/components/form/BuildingTypeSelector'
  import moment                   from 'moment'

  export default {

    components: {
      InputText,
      InputEmail,
      InputPhone,
      InputPostcode,
      TitleSelector,
      InputABN,
      InputRadio,
      InputDollarValue,
      StateSelector,
      CountrySelector,
      InputDatePickerReadOnly,
      RequiredAsterisk,
      EmploymentStatusSelector,
      StreetTypeSelector,
      BuildingTypeSelector
    },

    props : {
      employment : {
        type : Object,
        required : true
      },

      updateValue : {
        type : Function,
        required : true,
      },

      objectType : {
        type : String,
        required : false
      },

      index : {
        type : Number,
        required : false
      },

      deleteEmployment : {
        type    : Function,
        required: false
      },

      isCurrentLessThanThreeYears : {
        type : Boolean,
        required : false
      },

      getEmploymentDuration : {
        type : Function,
        required : false,
      },

      numPreviousEmployments : {
        type : Number,
        required : false
      },
    },

    computed : {
      isDateStartedLessThanSixMonths() {
        return moment(this.employment.date_started_at, "YYYY-MM-DD").add(6, 'months').unix() > moment().unix()
      },

      isStartedLaterThanEnded() {
        if(this.employment.date_started_at != null && this.employment.date_ended_at != null && this.employment.date_started_at > this.employment.date_ended_at) {
          return true
        }

        return false
      }
    }
  }
</script>